import { useEffect, useRef, useState } from 'react';

import { fetchAreaNetworkStats, fetchDmaNetworkStats, type NetworkStats } from '@/api/statsApi';
import { showErrorToast } from '@/errors/showErrorToast';
import { useStatsFilters } from '@/stats/StatsFiltersContext';

/**
 * A clicked Area of Interest or DMA whose stats card is shown over the map.
 * `lngLat` is the click point used to anchor the popup (not used for fetching).
 * AOIs are keyed by `name` (their composite-PK identifier); DMAs by `ident`.
 */
export type MapSelection =
    | { type: 'aoi'; name: string; lngLat: [number, number] }
    | { type: 'dma'; ident: string; name: string; lngLat: [number, number] };

/** The five stats rendered on a selection card. AOI and DMA share the same shape. */
export interface SelectionStats {
    leaksFound: number;
    /** "Volume Identified" — the same potentialSaving figure the stats bar renders. */
    potentialSaving: number | null;
    suitableForNoDigCount: number;
    noDigFixes: number;
    mainsPipesCount: number;
}

export interface SelectionStatsState {
    stats: SelectionStats | null;
    isLoading: boolean;
    isError: boolean;
}

const IDLE: SelectionStatsState = { stats: null, isLoading: false, isError: false };

/** The server identifier used both to fetch and to cache a selection's stats. */
function selectionKey(selection: MapSelection): string {
    return selection.type === 'aoi' ? selection.name : selection.ident;
}

/** The card renders a fixed subset; caching only it keeps entries small and shape-stable. */
function toSelectionStats(stats: NetworkStats): SelectionStats {
    return {
        leaksFound: stats.leaksFound,
        potentialSaving: stats.potentialSaving,
        suitableForNoDigCount: stats.suitableForNoDigCount,
        noDigFixes: stats.noDigFixes,
        mainsPipesCount: stats.mainsPipesCount,
    };
}

/**
 * Fetches the stats for the currently-selected AOI/DMA on demand, from the same scoped,
 * filter-aware endpoints the stats bar uses (`network/stats/area|dma`) — one counting
 * contract, so a selection card can never disagree with the bar or the table for the
 * same scope and filter state.
 *
 * Nothing is preloaded: a request fires only when a feature is clicked, keyed by the
 * active client, the selection and the committed filter state (a filter change refetches
 * so the card tracks what the map shows). Results are memoised in a ref-held `Map` so
 * re-clicking the same feature is served from cache with no refetch and no loading
 * flicker. The in-flight request is aborted when the selection changes, so a stale
 * response can never overwrite a newer one.
 */
export function useSelectionStats(selection: MapSelection | null, clientId: string | null): SelectionStatsState {
    const [state, setState] = useState<SelectionStatsState>(IDLE);
    const cacheRef = useRef<Map<string, SelectionStats>>(new Map());
    // The committed map filters, bridged up from MapView (inert default when no provider).
    const { filters, filtersKey } = useStatsFilters();

    // Derived primitives keep the effect from re-running on selection object identity
    // churn (lngLat changes on every click are irrelevant to the fetch).
    const type = selection?.type ?? null;
    const key = selection ? selectionKey(selection) : null;

    useEffect(() => {
        if (type == null || key == null || !clientId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setState(IDLE);
            return;
        }

        const cacheKey = `${clientId}:${type}:${key}:${filtersKey}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setState({ stats: cached, isLoading: false, isError: false });
            return;
        }

        const controller = new AbortController();
        // eslint-disable-next-line react-x/set-state-in-effect
        setState({ stats: null, isLoading: true, isError: false });

        const request =
            type === 'aoi'
                ? fetchAreaNetworkStats(clientId, key, 'overall', filters, controller.signal)
                : fetchDmaNetworkStats(clientId, key, 'overall', filters, controller.signal);

        request
            .then((data) => {
                const stats = toSelectionStats(data);
                cacheRef.current.set(cacheKey, stats);
                setState({ stats, isLoading: false, isError: false });
            })
            .catch((error: unknown) => {
                // The selection moved on (or unmounted) — discard this stale result silently.
                if (controller.signal.aborted) return;
                showErrorToast(error);
                setState({ stats: null, isLoading: false, isError: true });
            });

        return () => {
            controller.abort();
        };
        // `filters` identity changes exactly when filtersKey does — the provider bails out
        // on key-identical publishes — and a cache hit returns early, so listing it cannot
        // cause redundant fetches.
    }, [type, key, clientId, filtersKey, filters]);

    return state;
}
